import React from 'react';
import { Card, IconButton } from '@innovationnorway/component-library';
import {
  Button,
  Field,
  Heading,
  Input,
  Label,
  RichTextEditor,
  ValidationMessage,
} from '@innovationnorway/cl-react';
import { Controller } from 'react-hook-form';
import useAboutForm, { AboutData } from '../hooks/subforms/use-about-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';

const AboutPage = (props: SubFormProps<AboutData>) => {
  const { form, registerWithRules, rules } = useAboutForm(props);
  const { formState, getValues, setValue, control } = form;
  const { errors } = props;
  const { t: tServices } = useTranslation('services');

  const [newUrl, setNewUrl] = React.useState<string>('');
  const [newUrlText, setNewUrlText] = React.useState<string>('');

  const [keywords, setKeywords] = React.useState<string[]>(getValues('keywords') || []);
  const [newKeyword, setNewKeyword] = React.useState<string>('');

  return (
    <div>
      <Heading>{tServices('about.title')}</Heading>
      <div className="display--flex flex-direction--column gap--5 margin-top--2">
        <div>
          <Field>
            <Label>{tServices('about.serviceName.label')}</Label>
            <Field.Description data-size="sm">{tServices('about.serviceName.help')}</Field.Description>
            <Input {...registerWithRules('name')} />
            {(errors.name?.message || formState.errors.name?.message) && (
              <ValidationMessage>{errors.name?.message || formState.errors.name?.message}</ValidationMessage>
            )}
          </Field>
        </div>
        <div className="display--flex gap--5">
          <Field>
            <Label>{tServices('about.validFrom.label')}</Label>
            <Input type="date" {...registerWithRules('validFrom')} />
            {(errors.validFrom?.message || formState.errors.validFrom?.message) && (
              <ValidationMessage>
                {errors.validFrom?.message || formState.errors.validFrom?.message}
              </ValidationMessage>
            )}
          </Field>
          <Field>
            <Label>{tServices('about.validTo.label')}</Label>
            <Input type="date" {...registerWithRules('validTo')} />
            {(errors.validTo?.message || formState.errors.validTo?.message) && (
              <ValidationMessage>
                {errors.validTo?.message || formState.errors.validTo?.message}
              </ValidationMessage>
            )}
          </Field>
        </div>
        <div>
          <Controller
            control={control}
            name="servicePurpose"
            rules={rules.servicePurpose}
            render={({ field, fieldState }) => {
              return (
                <Field>
                  <Label>{tServices('about.purpose.label')}</Label>
                  <Field.Description data-size="sm">{tServices('about.purpose.help')}</Field.Description>
                  <RichTextEditor
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    rows={6}
                  />
                  {(fieldState.error?.message || errors.servicePurpose?.message) && (
                    <ValidationMessage>
                      {fieldState.error?.message || errors.servicePurpose?.message}
                    </ValidationMessage>
                  )}
                </Field>
              );
            }}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="description"
            rules={rules.description}
            render={({ field, fieldState }) => {
              return (
                <Field>
                  <Label>{tServices('about.description.label')}</Label>
                  <Field.Description data-size="sm">{tServices('about.description.help')}</Field.Description>
                  <RichTextEditor
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    namespace="ServiceDescriptionEditor"
                    rows={6}
                  />
                  {fieldState.error && (
                    <ValidationMessage>
                      {errors.description?.message || fieldState.error?.message}
                    </ValidationMessage>
                  )}
                </Field>
              );
            }}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="customerDescription"
            render={({ field, fieldState }) => {
              return (
                <Field>
                  <Label>{tServices('about.customerDescription.label')}</Label>
                  <Field.Description data-size="sm">
                    {tServices('about.customerDescription.help')}
                  </Field.Description>
                  <RichTextEditor
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    rows={6}
                    namespace="ServiceCustomerDescriptionEditor"
                  />
                  {fieldState.error && (
                    <ValidationMessage>
                      {errors.customerDescription?.message || fieldState.error?.message}
                    </ValidationMessage>
                  )}
                </Field>
              );
            }}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="keywords"
            rules={rules.keywords}
            render={({ field, fieldState }) => (
              <>
                <Field>
                  <Label>{tServices('about.keywords.label')}</Label>
                  <Field.Description data-size="sm">{tServices('about.keywords.help')}</Field.Description>
                  <Input
                    maxLength={500}
                    value={newKeyword}
                    onChange={(e) => {
                      setNewKeyword(e.target.value);
                    }}
                    onBlur={field.onBlur}
                  />
                  {fieldState.error?.message && (
                    <ValidationMessage>{fieldState.error.message}</ValidationMessage>
                  )}
                </Field>
                <div className="display--flex flex-direction--row flex-wrap--wrap gap--1 margin-top--2">
                  {getValues('keywords') !== undefined &&
                    getValues('keywords')?.map((keyword, index) => (
                      <span
                        key={index}
                        className="margin-right--1 padding-left--1 display--flex flex-direction--row align-items--center background-color--blue-50 border-radius--1"
                      >
                        {keyword}
                        <IconButton
                          iconName="fa-light fa-times"
                          className="margin-left--1 font-size--large"
                          onClick={() => {
                            const filteredKeywords = keywords.filter((_, i) => i !== index);
                            setKeywords(filteredKeywords);
                            setValue('keywords', filteredKeywords);
                            form.trigger('keywords');
                          }}
                        />
                      </span>
                    ))}
                </div>
                <div>
                  <Button
                    variant="secondary"
                    className="margin-top--2"
                    disabled={!newKeyword}
                    onClick={() => {
                      setKeywords([...keywords, newKeyword]);
                      setValue('keywords', [...keywords!, newKeyword]);
                      setNewKeyword('');
                      form.trigger('keywords');
                    }}
                    onBlur={field.onBlur}
                  >
                    {tServices('form.add')}
                  </Button>
                </div>
              </>
            )}
          />
        </div>
        <div>
          <Field>
            <Label className="font-weight--bold">{tServices('about.links.title')}</Label>
            <Field.Description data-size="sm">{tServices('about.links.help')}</Field.Description>
          </Field>
          <div className="display--flex gap--1 flex-direction--row margin-top--2">
            <Field className="width--35">
              <Label>{tServices('about.links.text')}</Label>
              <Input onChange={(e) => setNewUrlText(e.target.value)} />
            </Field>
            <Field className="width--65">
              <Label>{tServices('about.links.url')}</Label>
              <Input onChange={(e) => setNewUrl(e.target.value)} />
            </Field>
          </div>
          {getValues('externalLinks').length > 0 &&
            getValues('externalLinks').map((url, index) => {
              if (!url || !url.url || !url.text) return null;
              return (
                <Card key={index} className="margin-top--2" boxShadow>
                  <div className="display--flex align-items--center">
                    <a href={new URL(url.url).toString()} target="_blank" rel="noopener noreferrer">
                      {url.text}
                    </a>
                    <IconButton
                      iconName="fa-light fa-times"
                      className="margin-left--auto font-size--large"
                      onClick={() => {
                        setValue(
                          'externalLinks',
                          getValues('externalLinks').filter((_, i) => i !== index),
                        );
                      }}
                    />
                  </div>
                </Card>
              );
            })}
          <Button
            variant="secondary"
            className="margin-top--2"
            disabled={!newUrl || !newUrlText}
            onClick={() => {
              setValue('externalLinks', [
                ...getValues('externalLinks'),
                { text: newUrlText, url: !/^https?:\/\//i.test(newUrl) ? `https://${newUrl}` : newUrl },
              ]);
              setNewUrl('');
              setNewUrlText('');
            }}
          >
            {tServices('form.add')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
